import React, { useContext, useEffect, useRef } from 'react';
import styles from '../ProductOverview.module.scss';
import { Row, Col, MenuProps, Spin } from 'antd';
import CarbonEmissionScale from 'shared/components/CarbonEmissionScale';
import { LeftCircleOutlined } from '@ant-design/icons';
import { initSocket } from 'shared/utils/initSocket';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import EmissionProvider from 'shared/components/EmissionProvider';
import { ProductOverviewEnums } from 'enums/ProductOverviewEnums';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { SocketContext } from 'context/SocketContext';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import { DownloadFileName, DownloadKeys } from 'enums/GraphDropdownEnums';
import { downloadProductMap } from 'shared/utils/HelperFunctions';
import ProductFlowMap from '../ProductFlowMap';
import clsx from 'clsx';
import { ProductMapRoots } from 'enums/ProductFlowMapEnums';
import DashboardService from 'services/DashboardService/dashboard.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';
import EmissionService from 'services/ProductsService/emission.service';

const DownloadMap = () => {
  const navigate = useNavigate();
  let productId: string | undefined;
  const location = useLocation();
  const { id } = useParams();

  productId = id;
  let inCreationPhase = location.state?.inCreationPhase === true ? true : false;
  const {
    fetchProductEmissionsStage1AndStage2,
    fetchProductEmissionsStage3,
    fetchProductEmissionsStage4,
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    checkTotalEmissionStatus,
  } = EmissionService();
  const {productOverviewLoader, setProductOverviewLoader, productLoading, fetchSingleProduct, newProduct } =
    ProductCreationService();
  const { downloadGraphCsv } = DashboardService();
  const { setSocketConnection, socket } = useContext(SocketContext);
  useEffect(() => {
    const token = localStorage.getItem('user');
    socket?.disconnect();
    const socketDetails = initSocket(token);
    setSocketConnection(socketDetails);
  }, []);

  useEffect(() => {
    if (!productLoading) fetchSingleProduct(Number(productId));
    if (socket) {
      socket.on('emission-calculation', () => {
        fetchProductEmissionsStage1AndStage2(productId);
        fetchProductEmissionsStage3(productId);
        fetchProductEmissionsStage4(productId);
      });

      return () => {
        socket.off('emission-calculation', () => {});
      };
    }
  }, [socket]);

  useEffect(() => {
    if (!newProduct) return;

    if (newProduct.status === 'draft') {
      checkTotalEmissionStatus();
    } else {
      fetchProductEmissionsStage1AndStage2(productId);
      fetchProductEmissionsStage3(productId);
      fetchProductEmissionsStage4(productId);
    }
  }, [newProduct]);

  const handleGoBack = () => {
    if (inCreationPhase) {
      navigate(
        generatePath(
          `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PRODUCT_DISTRIBUTION}`,
          { id: id }
        )
      );
    } else if (!inCreationPhase) {
      navigate(-1);
    }
  };
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDownload: MenuProps['onClick'] = ({ key }) => {
    if (key === DownloadKeys.JPG || key === DownloadKeys.PNG) {
      setProductOverviewLoader(true);
      setTimeout(async () => {
        await downloadProductMap(
          containerRef,
          key,
          DownloadFileName.PRODUCT_LIFE_CYCLE_MAP
        );
        setProductOverviewLoader(false);
      }, 150);
    } else if (key === DownloadKeys.CSV) {
      downloadGraphCsv(id);
    }
  };

  return (
    <>
      <EmissionProvider>
        <div
          className={clsx(
            productOverviewLoader && styles.loaderOverviewContainer,
            styles.overviewDownloadContainer
          )}
          id={ProductMapRoots.PRODUCT_MAP_DOWNLOAD_ROOT}>
          {productOverviewLoader && (
            <div className={styles.loaderContainer}>
              <Spin spinning />
            </div>
          )}
          <Row>
            <Col span={23}>
              <div className={styles.createProductTopRow}>
                <div className={styles.topLeftContainerMap}>
                  <div
                    className={styles.backButtonContainer}
                    onClick={handleGoBack}>
                    <LeftCircleOutlined />
                  </div>
                  <h2>{ProductOverviewEnums.DOWNLOAD}</h2>
                </div>

                <div className={styles.topRightContainer}>
                  <GraphDropDown
                    handleDownload={handleDownload}
                    graphRef={containerRef}
                    downloadCsv={downloadGraphCsv}
                    showSort={false}
                    downloadMap={true}
                  />
                </div>
              </div>

              <div ref={containerRef} className={styles.whiteBg}>
                <div className={styles.carbonEmissionScaleContainer}>
                  <CarbonEmissionScale />
                </div>
                <div className={styles.downloadMappingContainer}>
                  {stage1Emissions &&
                    stage2Emissions &&
                    stage3Emissions &&
                    stage4Emissions && (
                      <ProductFlowMap
                        stage1Emissions={stage1Emissions}
                        stage2Emissions={stage2Emissions}
                        stage3Emissions={stage3Emissions}
                        stage4Emissions={stage4Emissions}
                        isDownloadPage
                        setProductOverviewLoader={setProductOverviewLoader}
                        rootId={ProductMapRoots.PRODUCT_MAP_DOWNLOAD_ROOT}
                      />
                    )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </EmissionProvider>
    </>
  );
};

export default DownloadMap;
